import type { Dispatch, SetStateAction } from 'react';
import type { SortType } from 'rsuite';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';
import type { DataGridColumnProps, DataGridProps } from '../DataGrid/DataGrid';
import type { DataGridColumnOptionsProps } from '../DataGridControl/DataGridControl';
import type { PageChangeFn } from '../Pagination/Pagination';

import { useCallback } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { resizeColumn } from '../../lib/helpers';

import DataGrid from '../../components/DataGrid/DataGrid';

import useDebounceCallback from '../../hooks/useDebounceCallback';

import Loader from '../Loader/Loader';

interface CustomizableDataGridProps extends DataGridProps {
  /**
   * Function to handle columns change
   */
  setColumns?: Dispatch<SetStateAction<DataGridColumnProps[]>>;
  /**
   * Local storage key for sorting
   */
  sortingLocalStorageKey?: string;
  /**
   * Local storage key for grid options
   */
  optionsLocalStorageKey?: string;
  /**
   * Function to handle current page change
   */
  setCurrentPage?: PageChangeFn;
  /**
   * If grid is currently editing
   */
  editGrid?: boolean;
  /**
   * DataGrid no data info text
   */
  noDataInfoText?: string;
}

const CustomizableDataGrid = ({
  setColumns = EMPTY_FUNCTION,
  sortingLocalStorageKey = '',
  optionsLocalStorageKey = '',
  setCurrentPage = EMPTY_FUNCTION,
  editGrid = false,
  noDataInfoText,
  ...props
}: CustomizableDataGridProps) => {
  const [, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(
      sortingLocalStorageKey,
    );
  const [gridOptions, setGridOptions] = useLocalStorageState<
    DataGridColumnOptionsProps[]
  >(optionsLocalStorageKey);

  const handleSort = useCallback(
    (sortField?: string, sortOrder?: SortType) => {
      if (sortField && sortOrder) {
        setSort({ sortField, sortOrder });
      } else {
        removeSort();
      }
      setCurrentPage(1);
    },
    [removeSort, setCurrentPage, setSort],
  );

  const handleLocalstorageGridOptionsOrder = useCallback(
    (columns: DataGridColumnProps[]) => {
      if (!gridOptions) return;

      const updateOrder = [];

      for (const item in columns) {
        const indexSaved = gridOptions.findIndex(
          (el) => el.colId === columns[item].accessor,
        );
        if (indexSaved > -1) {
          updateOrder.push(gridOptions[indexSaved]);
        }
      }

      setGridOptions(updateOrder);
    },
    [gridOptions, setGridOptions],
  );

  const debouncedGridOptionsOrder = useDebounceCallback(
    handleLocalstorageGridOptionsOrder,
    10,
  );

  const handleColumnsOrder = useCallback(
    (sourceAccessor: string, targetAccessor: string) => {
      if (sourceAccessor === targetAccessor) return;
      setColumns((oldColumns: DataGridColumnProps[]) => {
        const targetIdx = oldColumns.findIndex(
          (column) => column.accessor === targetAccessor,
        );
        const sourceIdx = oldColumns.findIndex(
          (column) => column.accessor === sourceAccessor,
        );
        const newCols = [...oldColumns];
        newCols.splice(targetIdx, 0, newCols.splice(sourceIdx, 1)[0]);
        debouncedGridOptionsOrder(newCols);
        return newCols;
      });
    },
    [debouncedGridOptionsOrder, setColumns],
  );

  const debouncedOnColumnsOrder = useDebounceCallback(handleColumnsOrder, 1);

  const handleColumnResize = useCallback(
    (columnWidth: number, dataKey: string) =>
      resizeColumn(columnWidth, dataKey, gridOptions, setGridOptions),
    [gridOptions, setGridOptions],
  );

  const debouncedOnColumnResize = useDebounceCallback(handleColumnResize, 100);

  const handleOnRemoveColumn = useCallback(
    (id: string) => {
      if (!gridOptions) return;

      const columnIndex = gridOptions.findIndex((el) => el.colId === id);

      if (columnIndex > -1) {
        const updateGridOptions = [...gridOptions];
        updateGridOptions[columnIndex].hide = true;
        setGridOptions(updateGridOptions);
      }
    },
    [gridOptions, setGridOptions],
  );

  return (
    <div className="h-[calc(100%-40px)] md:h-[calc(100%-72px)]">
      <DataGrid
        {...props}
        autoHeight={false}
        fillHeight
        onSortColumn={handleSort}
        loadingIcon={<Loader size={'small'} type={'spinner-grid'} />}
        onDrop={editGrid ? debouncedOnColumnsOrder : undefined}
        onResize={debouncedOnColumnResize}
        onRemoveColumn={handleOnRemoveColumn}
        removableColumns={editGrid}
        noDataInfo={
          <span className="flex items-center justify-center w-full h-full">
            {noDataInfoText}
          </span>
        }
      />
    </div>
  );
};

export default CustomizableDataGrid;
